import { usePassportProvider } from "@/context";
import { useAccessTokenState } from "@/hooks";
import { authorizedGet } from "@/utils/request";
import { useEffect, useRef, useState } from "react";
import useSWR from "swr";

export interface PrizeWon {
  name: string;
  tier: number;
  wallet_address: string;
}

interface PrizesWonFeedResponse {
  result: PrizeWon[];
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const usePrizesWonFeed = () => {
  const accessToken = useAccessTokenState();
  const { isLoggedIn } = usePassportProvider();
  const [currentPrizeIndex, setCurrentPrizeIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Track displayed prizes to know when we've cycled through all
  const displayedPrizesSet = useRef(new Set<number>());
  const lastFetchTime = useRef<number>(Date.now());
  const hasTriggeredRefetch = useRef(false);

  const {
    data: prizesWon,
    isLoading,
    error,
    mutate,
  } = useSWR<PrizeWon[]>(
    accessToken && isLoggedIn ? ["prizes-won-feed", accessToken] : null,
    async () => {
      if (!accessToken) {
        throw new Error("No access token");
      }
      const response = await authorizedGet<PrizesWonFeedResponse>(
        "/v2/rewards/spin/prizes-won-feed",
        accessToken,
      );
      lastFetchTime.current = Date.now();
      return response.result || [];
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: CACHE_DURATION, // Refresh every 5 minutes
      dedupingInterval: CACHE_DURATION, // Dedupe requests within 5 minutes
    },
  );

  // Fetch available spins
  const { data: spinsData, isLoading: isLoadingSpins } = useSWR<{
    availableSpins?: number;
  }>(
    accessToken && isLoggedIn ? ["available-spins", accessToken] : null,
    async () => {
      if (!accessToken) {
        throw new Error("No access token");
      }
      return authorizedGet<{ availableSpins?: number }>(
        "/v2/rewards/spin/available-spins",
        accessToken,
      );
    },
  );

  const spinsAvailable = spinsData?.availableSpins || 0;

  // Reset displayed prizes tracking when new data arrives
  useEffect(() => {
    if (prizesWon) {
      displayedPrizesSet.current.clear();
      // Mark the initial prize as displayed
      displayedPrizesSet.current.add(0);
      setCurrentPrizeIndex(0);
      // Reset refetch trigger flag for new data
      hasTriggeredRefetch.current = false;
    }
  }, [prizesWon]);

  // Rotate through prizes every 5-20 seconds with fade transition
  useEffect(() => {
    if (!prizesWon || prizesWon.length === 0) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const getRandomInterval = () => {
      // Random interval between 5-20 seconds
      return Math.floor(Math.random() * (20000 - 5000 + 1)) + 5000;
    };

    const rotatePrize = () => {
      // Start scroll up and fade out
      setIsTransitioning(true);

      // After scroll up completes, change prize and scroll in
      setTimeout(() => {
        setCurrentPrizeIndex((prev) => {
          const nextIndex = (prev + 1) % prizesWon.length;

          // Track this prize as displayed
          displayedPrizesSet.current.add(nextIndex);

          // Check if we've displayed all prizes
          if (
            displayedPrizesSet.current.size === prizesWon.length &&
            !hasTriggeredRefetch.current
          ) {
            // Check if 5 minutes haven't passed yet
            const timeSinceLastFetch = Date.now() - lastFetchTime.current;
            if (timeSinceLastFetch < CACHE_DURATION) {
              // All prizes shown before 5 minutes - trigger refetch once
              hasTriggeredRefetch.current = true;
              mutate();
            }
          }

          return nextIndex;
        });
        setIsTransitioning(false);
      }, 500); // Match the CSS transition duration
    };

    const scheduleNextRotation = () => {
      timeoutId = setTimeout(() => {
        rotatePrize();
        scheduleNextRotation(); // Schedule next rotation with new random interval
      }, getRandomInterval());
    };

    scheduleNextRotation();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [prizesWon, mutate]);

  const currentPrize =
    prizesWon && prizesWon.length > 0 ? prizesWon[currentPrizeIndex] : null;

  return {
    prizesWon,
    currentPrize,
    isTransitioning,
    isLoading: isLoading || isLoadingSpins,
    error,
    mutate,
    spinsAvailable,
  };
};
